import { useMemo, useRef, useState } from "react";
import type { ChipOption } from "./ChipSet";

type Props = {
  options: ChipOption[];
  value: string[] | undefined;
  onChange: (next: string[] | undefined) => void;
  label: string;
  placeholder?: string;
};

/** Searchable multi-select for long option lists (states, balancing authorities, operators). */
export function MultiPick({ options, value, onChange, label, placeholder }: Props) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const selected = value ?? [];
  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    const list = t
      ? options.filter(
          (o) => o.label.toLowerCase().includes(t) || o.value.toLowerCase().includes(t),
        )
      : options;
    return list.slice(0, 40);
  }, [options, term]);

  const add = (v: string) => {
    if (selected.includes(v)) return;
    onChange([...selected, v]);
    setTerm("");
  };
  const remove = (v: string) => {
    const next = selected.filter((s) => s !== v);
    onChange(next.length === 0 ? undefined : next);
  };

  return (
    <div className="relative" ref={wrapRef}>
      {selected.length > 0 ? (
        <div className="mb-1 flex flex-wrap gap-1">
          {selected.map((v) => (
            <button
              key={v}
              type="button"
              className="chip"
              aria-pressed="true"
              onClick={() => remove(v)}
              title="Remove"
            >
              {options.find((o) => o.value === v)?.label ?? v} ×
            </button>
          ))}
        </div>
      ) : null}
      <input
        className="input w-full"
        value={term}
        placeholder={placeholder ?? "Type to filter"}
        aria-label={label}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filtered[0]) {
            e.preventDefault();
            add(filtered[0].value);
          }
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && filtered.length > 0 ? (
        <ul
          className="scroll-thin absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-auto rounded border py-1"
          style={{
            background: "var(--panel)",
            borderColor: "var(--line-2)",
            boxShadow: "var(--shadow)",
          }}
        >
          {filtered.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                className="flex w-full items-center justify-between px-2 py-1 text-left hover:bg-[var(--panel-2)]"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(o.value)}
                disabled={selected.includes(o.value)}
                style={{ color: selected.includes(o.value) ? "var(--muted-2)" : "var(--paper)" }}
              >
                <span>{o.label}</span>
                {o.count !== undefined ? (
                  <span className="num" style={{ color: "var(--muted-2)" }}>
                    {o.count}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
